// VITE_MIGRATION: explicit deps so transitively-imported tool files see
// Helpers/Templates/Row before this IIFE pulls them from Sparkle.PageBuilder
// at module-eval time.
import { decorateFormComponent } from "#/admin/legacy/form-builder/page-builder-form.js";
import "./namespace.js";
import "./helpers.js";
import "./templates.js";
import "./models/row.js";
import "./models/component/action_bar.js";

(function (Row, assert, deepCopy, numColumns, findTemplate, Event, moment, toolKeysForComponent, _) {
  "use strict";

  /**
   * given the original component data, and the expanded component data, as
   * well as a list of definitions of the various components, we can
   * superimpose the expanded version of images into the context data
   * for all the components on the page, without also injecting other
   * expanded data into those component contexts.
   * @param {object} componentData - Unexpanded component data.
   * @param {object} expandedComponentData - Expanded component data.
   * @param {object} componentDefinitions - Description of all components.
   * @returns {object} - Component data, but with expanded images.
   **/
  function expandImagesInContext(context, rendered, config) {
    rendered = rendered || {};

    // there are two methods we use to superimpose the expanded image
    // data on the original data before initializing page builder.

    // 1. expanding individual image attributes.

    // for each of the attributes of this component that are images..
    var imageKeys = toolKeysForComponent(config, "image-editor");

    imageKeys.forEach(function (imageKey) {
      if (context[imageKey] && rendered[imageKey] && context[imageKey].id === rendered[imageKey].id) {
        context[imageKey] = rendered[imageKey];
      }
    });

    // 2. replacing entire arrays of images in a slideshow.
    // there is only one component that uses the slideshow tool at the
    // time of this writing, but this will automatically acccount for any
    // new components that also use the slideshow tool.
    var slideshowKeys = toolKeysForComponent(config, "slideshow");

    slideshowKeys.forEach(function (slideshowKey) {
      // replace the normal image data with expanded image data.
      if (context[slideshowKey] && rendered[slideshowKey]) {
        context[slideshowKey] = rendered[slideshowKey];
      }
    });
  }

  function hex(n) {
    n = n || 10;
    var result = "";

    while (n--) {
      result += Math.floor(Math.random() * 16).toString(16);
    }

    return result;
  }

  function nameSplit(name) {
    return name.split("\.");
  }

  function pluck(name, context) {
    var returnValue = context;

    nameSplit(name).forEach(function (key) {
      returnValue = returnValue[key];
    });

    return returnValue;
  }

  function deepDelete(name, obj) {
    var updateObject = obj || {},
      split = nameSplit(name);

    while (split.length > 1) {
      updateObject = updateObject[split.shift()];
    }

    delete updateObject[split[0]];
  }

  function deepUpdate(name, obj, value) {
    var updateObject = obj || {},
      split = nameSplit(name),
      value = deepCopy(value);

    while (split.length > 1) {
      updateObject = updateObject[split.shift()];
    }

    updateObject[split[0]] = value;
  }

  /**
   * The Section represents a piece of the page that contains
   * components. The Section object is how components are added,
   * removed, and inserted into the page.
   * This is generally created one time on page load when the
   * page data is passed into the initialize function.
   * @param {string} name - section name
   * @param {integer} pageColumns - the global, page level number of columns a section uses
   * @param {object} data - section data. Currently just a list of components, and this
   * isn't used here at all.
   * @param {HTMLElement} element - Sections element
   **/
  function Section(name, element, componentsPerRow) {
    assert(name, "Sections must be created with a name.");

    // set the minimum number of columns based on how
    // big the section is relative to the layout. The bigger
    // it is, the smaller the component can be resized to.
    var minColumns = 1;
    var maxColumns = window.SparkleConfig.pageBuilder.maxColumnsPerRow;
    componentsPerRow = componentsPerRow || maxColumns; // Default max number of components to the maximum amount of columns

    function validColumn(component, columns) {
      /*
       * Components can set a minColumns value in config. These columns
       * are relative to the overall page, usually 4, 8 or 12. If you try to set
       * the component columns to something greater than minColumns, it will fail.
       *
       * Every section has 12 columns, components can take up as many as they want, but not
       * less than the minimum number, defined above. This is the second way inserting a component
       * can fail.
       */

      if (component.config.minColumns > maxColumns || columns < minColumns) {
        return false;
      }
      assert(
        !!columns && typeof columns === "number" && columns <= maxColumns,
        "You must set columns to be a lower or equal value to " + maxColumns,
      );

      return true;
    }

    // When a component is added to a section, it can
    // be resized automatically depending on defaults.
    // That logic lives here.
    function setInitialColumns(component) {
      component.setInitialColumns(minColumns);
    }

    var section = {
      name: name,
      element: element,
      components: [],
      hiddenComponents: [],
      rows: [],
      maxColumns: maxColumns,
      minColumns: minColumns,
      componentsPerRow: componentsPerRow,

      /**
       * Reflow the dom elements without touching the data
       * @memberof page-builder.Section
       **/
      reflow: function (children) {
        // only touch the dom, don't update component data
        var $children = jQuery(this.element).children(),
          children = children || $children.toArray(),
          totalColumns = 0;

        var prevElement;
        children.forEach(function (element) {
          element.classList.remove("omega");
          var columns = numColumns(element);
          totalColumns += columns;
          if (totalColumns === maxColumns) {
            totalColumns = 0;
            element.classList.add("omega");
          } else if (totalColumns > maxColumns) {
            // this is a row with components that don't fill
            // the row but adding the next component won't fit
            totalColumns = columns;
            prevElement.classList.add("omega");
          }
          prevElement = element;
        });
      },

      createRows: function () {
        // have to loop through elements instead of me.components
        // because inherited components are not in the data.

        var me = this,
          componentElems = jQuery(me.element).find(".component").toArray(),
          newRow;

        me.rows = [];

        if (componentElems.length > 0) {
          newRow = new Row(me);

          componentElems.forEach(function (elem) {
            var component = Component.find(elem);
            // skip inherited components
            if (component) {
              newRow.components.push(component);
              component.row = newRow;
            }
            newRow.elems.push(elem);
            if (elem.classList.contains("omega")) {
              me.rows.push(newRow);
              newRow = new Row(me);
            }
          });
        } else {
          newRow = new Row(me, true);
          me.rows.push(newRow);
        }
      },

      canInsertAfter: function (row) {
        var index = this.rows.indexOf(row),
          nextRow = this.rows[index + 1];

        if (index === this.rows.length - 1) {
          // can always insert at the end of a section
          return true;
        } else if (nextRow && nextRow.components.length > 0) {
          // if there are no components in the next row that means
          // it only has inherited components, so can't insert there.
          return true;
        } else {
          return false;
        }
      },

      canInsertBefore: function (row) {
        // as long as there are non-inherited components in this row,
        // can insert before
        return row.components.length > 0;
      },

      newRowAfter: function (row) {
        var index = this.rows.indexOf(row) + 1,
          newRow = new Row(this);
        newRow.createElement();
        this.rows.splice(index, 0, newRow);
        return newRow;
      },

      newRowBefore: function (row) {
        var index = this.rows.indexOf(row) - 1,
          newRow = new Row(this);
        newRow.createElement();
        this.rows.splice(index, 0, newRow);
        return newRow;
      },

      /**
       * Add a component to the end of a section. This is only
       * used when initializing. setInitialColumns is not called,
       * if it is called then the columns will be changed on the initial
       * load to default columns. Don't do that.
       * @memberof page-builder.Section
       * @param {Component} component - the component to add
       */
      add: function (components) {
        var me = this;
        function add(component, previous) {
          if (component.hidden) {
            me.hiddenComponents.push({
              previous: previous ? previous.key : null,
              component: component,
            });
          } else {
            me.components.push(component);
          }
          component.section = me;
          Event.trigger("pb:section:add", component);
        }

        var previous = null;
        components.forEach(function (component) {
          add(component, previous);
          previous = component;
        });
      },

      prepend: function (component) {
        if (validColumn(component, component.columns())) {
          this.components.unshift(component);
          component.section = this;
          Event.trigger("pb:section:add", component);
          return component;
        } else {
          return false;
        }
      },

      /**
       * Insert a component into a new position in this section. Doesn't allow
       * you to insert if the component doesn't fit into the section. Resizes components
       * if needed.
       * @memberof page-builder.Section
       * @param {integer} position - The new position to insert the component into
       * @param {page-builder.Component} - The component to insert
       * @param {object} callbacks - Either 'success' or 'failure' will be called when present.
       * @param {object} options - Skip validation if 'skipValidation' is true.
       *
       */
      insert: function (position, component, callbacks, options) {
        var success, failure;

        if (!options) {
          options = {};
        }

        if (!options.skipValidation) {
          setInitialColumns(component);
        }

        if (callbacks) {
          success = callbacks.success;
          failure = callbacks.failure;
        }

        if (!options.skipValidation) {
          if (!validColumn(component, component.columns())) {
            // error message here
            Sparkle.PageBuilder.Dialog.show({
              title: "Component doesn't fit there",
              text: "The component you are trying to add doesn't fit in the section" + " you are trying to add it to.",
              type: "error",
            });

            if (failure) {
              failure(this, component);
            }
            return false;
          }
        }

        this.components.splice(position, 0, component);
        component.section = this;
        Event.trigger("pb:section:add", component);
        if (success) {
          success(this, component);
        }
        return component;
      },

      empty: function () {
        return this.components.length === 0;
      },

      /**
       * Remove a component from the section.
       * @memberof page-builder.Section
       * @param {page-builder.Component} - The component to insert
       */
      remove: function (component) {
        // only attempt to remove the component if it's in the array.
        if (_.contains(this.components, component)) {
          this.components.splice(component.position(), 1);
        }
        Event.trigger("pb:section:remove", component);
      },

      // there is a difference between deleting and just removing in order to
      // move a component to a different section or part of the same section
      // so to delete, we call completeRemove
      completeRemove: function (component) {
        // to work with legacy replacement components, don't add to hiddenComponents or mark
        // as deleted.
        if (component.inherited) {
          if (component.markHidden && !component.replacement) {
            var previous = this.components[component.position() - 1];
            component.markHidden();
            this.hiddenComponents.push({
              previous: previous ? previous.key : null,
              component: component,
            });
          }
        }
        this.remove(component);
        Event.trigger("pb:section:completeremove", component);
      },

      /**
       * Return an object representing this Section that can be turned into json
       * and stored on the server.
       * @memberof page-builder.Section
       */
      data: function () {
        var data = [],
          plusDeleted = this.components.map(function (c) {
            return c;
          }),
          keys = this.components.map(function (component) {
            return component.key;
          });

        this.hiddenComponents.forEach(function (deleted) {
          var index,
            keys = plusDeleted.map(function (component) {
              return component.key;
            });

          if (deleted.previous) {
            var index = keys.indexOf(deleted.previous);
          } else {
            index = -1;
          }
          plusDeleted.splice(index + 1, 0, deleted.component);
        });

        plusDeleted.forEach(function (component) {
          data.push(component.data());
        });

        return data;
      },
    };
    Section.instances[name] = section;
    return section;
  } // end section

  /**
   * All sections are created in the initialize function. Subsequent requests
   * to get a section call this function.
   * @static
   * @memberof page-builder.Section
   * @param {string} name - Section name to retrieve
   */
  Section.get = function (name) {
    if (name) {
      if (Section.instances[name]) {
        return Section.instances[name];
      } else {
        var section = Section(name);
        Section.instances[name] = section;
        return section;
      }
    } else {
      return false;
    }
  };
  /**
   * Section instances can be retrieved from a component element based on
   * the data-component-section value on the section element
   * @param {HTMLElement} element - the component element
   * @returns {Section}
   */
  Section.find = function (element) {
    if (element && element.parentNode) {
      var name = element.parentNode.dataset.componentSection;
      return Section.get(name) || false;
    } else {
      return false;
    }
  };
  Section.instances = {};

  // create new rows for every section on the page
  Section.createRows = function () {
    Row.clear();
    _.values(Section.instances).forEach(function (section) {
      section.createRows();
    });

    return Row.rows;
  };

  Section.hasHidden = function () {
    var hasHidden = false;
    _.values(Section.instances).forEach(function (section) {
      if (section.hiddenComponents.length > 0) {
        hasHidden = true;
      }
    });
    return hasHidden;
  };

  Section.unhideAll = function () {
    _.values(Section.instances).forEach(function (section) {
      if (section.hiddenComponents.length > 0) {
        section.hiddenComponents.forEach(function (deleted) {
          var prevElem = jQuery(section.element).find("#component_" + deleted.previous)[0],
            previous = Component.find(prevElem),
            component = deleted.component,
            position = previous ? previous.position() + 1 : 0;
          component.render();
          section.insert(position, component);
          if (previous) {
            jQuery(previous.element).after(component.element);
          } else {
            jQuery(section.element).prepend(component.element);
          }
          component.unmarkHidden();
        });
        section.hiddenComponents = [];
        section.reflow();
      }
    });
  };

  /**
   * Creates a new Page. A page manages splinters, handles
   * rolling back changes, tracks the currently loaded component,
   * loads and unloads components
   * @memberof page-builder
   * @constructor
   * @param {HTMLElement} element - Element that contains the page body being edited
   * @returns {Page}
   */
  function Page(element, config) {
    var page = {
      element: element,
      iframe: element.ownerDocument.defaultView.frameElement,
      config: config,
      component: null,
      section: null,
      splinters: {},
      /**
       * Saves the given component changes
       * @memberof page-builder.Page
       */
      save: function () {},
      rollback: function () {
        this.component.rollback();
        this.component.render();
        _.values(this.splinters).forEach(function (splinter) {
          splinter.reset();
        });
      },
      load: function (component) {
        this.unload();
        this.component = component;
        this.component.element.classList.add("component-active");

        this.component.render();

        var attributes = this.component.config.attributes,
          me = this;
        _.keys(attributes).forEach(function (prop) {
          me.splinters[prop] = me.component.splinter(prop);
        });
        this.component.stamp("head");
        Event.trigger("pb:component:load", this.component);
        return this.component;
      },
      unload: function () {
        /* Always save when we unload. In every case I can think of, this
          is what we want to do. In the case of cancelling changes, we rollback
          before we unload. In the case of starting a reorder, where we unload
          the current component, they would probably be surprised to go back to
          edit that component, then hit cancel and it removes stuff they had done
          the last time they edited this component.
        */
        this.save();
        if (this.component) {
          this.component.element.classList.remove("component-active");
          this.component = null;
          Event.trigger("pb:component:unload", this.component);
        }
        this.splinters = {};
      },
      /**
       * Retrieve a deep copy of the component data
       * @memberof page-builder.Page
       * @param {integer} position - The components position in the component data array
       * @returns {object}
       */
      data: function (sectionName, position) {
        return Page.data(sectionName, position);
      },
      splinter: function (name) {
        return this.splinters[name] || this.component.splinter();
      },
      toJson: function () {
        return JSON.stringify(this.data());
      },
    };
    return page;
  } // end page

  /**
   * Returns an object representing all of the sections and components, or
   * returns just a section or a component's data, depending on the parameters.
   * The "data" can be turned into json to be stored on the server.
   * @static
   * @memberof page-builder.Page
   * @param {string} sectionName - name of the section to return
   * @param {string} position - Position of the component to return
   */
  Page.data = function (sectionName, position) {
    var data = {},
      section,
      component;

    if (sectionName && position >= 0) {
      section = Section.get(sectionName);
      component = Section.components[position];
      data = component.data();
      assert(!!data, "Can't find component data at position: " + position);
    } else if (sectionName) {
      section = Section.get(sectionName);
      data = section.data();
      assert(!!data, "Can't find component data for section: " + section);
    } else {
      _.values(Section.instances).forEach(function (section) {
        data[section.name] = section.data();
      });
    }
    return deepCopy(data);
  };

  /**
   * Converts the data to json
   * @static
   * @memberof page-builder.Page
   */
  Page.toJson = function () {
    return JSON.stringify(Page.data());
  };

  /**
   * @constructor
   * @memberof page-builder
   * @param {integer} position - Position of the component
   * @param {object} data - Data associated with the component, retrieved via page.data()
   * @param {HTMLElement} element - The component's element
   * @returns {Component}
   */
  function Component(data, config, element) {
    assert(!!data, "Could not find component data for " + data.template);
    assert(!!config, "Could not find component definition for " + data.template);

    /**
     * Creates a snapshot of the data that is stored in a private history array. The object
     * returned is like {templateId: 'template-name-id', context: {}, classes: [], firstSave: false}
     *
     * Classes are a deep copy. The context object uses the current component context as a prototype
     * so changes use prototypical inheritance as a kind of "delta" for properties.
     * @param {object} data - The data for the component, must at least have templateId, context,
     * and classes properties
     * @returns {object}
     */
    function createSnapshot(data) {
      var data = deepCopy(data);
      // context is sometimes null, e.g. for shared components with no changes,
      // so we need to make sure this is an object, many functions assume an object
      // and blow up on null.
      data.context = data.context || {};
      return data;
    }

    var first = createSnapshot(data),
      current = createSnapshot(first),
      history = [{ stamps: [], snapshot: first }];

    var actionBar = new Sparkle.PageBuilder.Component.ActionBar();
    var component = {
      /**
       * The key is set from the data. The key is only ever generated on the server. For new
       * components, we create a temporary key but never set it on the data. We set that separately
       * in Component.fromConfig. Since it's only on the object instance, and not data, it will never
       * get sent up to the server, when the server sees an empty key, it will generate a new one.
       */
      key: data.key,
      hidden: !!data.hidden,
      section: null,
      config: config,
      resizeable: config.resizeable,
      context: current.context || {},
      template: findTemplate(data.template + "-template"),
      resizable: config.resizable,
      element: element,
      replaces: data.replaces,
      replacement: !!data.replaces,
      created_at: data.created_at,
      updated_at: data.updated_at,
      empty: false,
      inherited: data.inherited,

      anchorWithDefault: function () {
        return jQuery(element).attr("id");
      },

      getLabel: function () {
        var title = this.context.title;
        if (typeof title === "object" && title !== null) {
          title = title.text;
        }

        return title || componentIdentityText(this);

        function componentIdentityText(self) {
          return truncate(jQuery(self.context.text), 5) || self.config.name + " (" + self.key + ")";
        }

        function truncate(content, number) {
          return content.first("p").text().split(/\s+/).slice(0, number).join(" ");
        }
      },

      columns: function () {
        return this.data("columns");
      },

      minimumColumns: function () {
        if (this.config.resizeable) {
          return 4;
        } else {
          return this.config.columns;
        }
      },

      position: function () {
        if (this.section) {
          return this.section.components.indexOf(this);
        } else {
          return -1;
        }
      },

      data: function (name, value) {
        if (_.isUndefined(name)) {
          return deepCopy(current);
        } else if (_.isUndefined(value)) {
          return current[name];
        } else {
          current[name] = value;
        }
      },

      /**
       * Add a class to the component which will be added to the component element when it's rendered
       * @memberof page-builder.Component
       * @param {string} className - CSS class to add to component
       */
      addClass: function (className) {
        // Some options may not apply a class because they are the default, in those cases we can bail out early.
        if (!className) {
          return;
        }

        current.classes.push(className);
        // so we can test without elements
        if (this.element) {
          this.element.classList.add(className);
        }
      },

      // we need a way for the `Blended` decorator to be able to
      // manipulate the current data without just keeping a
      // reference to it there.
      getCurrent: function () {
        return current;
      },

      getClasses: function () {
        return deepCopy(current.classes);
      },

      /**
       * @memberof page-builder.Component
       * @param {string} className - CSS class to remove from component
       */
      removeClass: function (className) {
        // Some options may not apply a class because they are the default, in those cases we can bail out early.
        if (!className) {
          return;
        }

        var index = current.classes.indexOf(className);
        if (index >= 0) {
          current.classes.splice(index, 1);
        }

        // in some instances, like shared components, the class may be on
        // the element even if it wasn't in the classes list, so we force
        // it's removal.
        if (this.element) {
          this.element.classList.remove(className);
        }
      },

      setAttribute: function (name, value) {
        // there is not always an element, e.g. for testing
        // and other situations.
        if (this.element) {
          this.element.setAttribute(name, value);
        }
      },

      /*
       * Does no validation of the columns, can be set to whatever.
       * checking values happens in the section.
       */
      setColumns: function (numCols) {
        var changed = this.columns() !== numCols;
        current.columns = numCols;
        this.setAttribute("data-columns", numCols);
        return changed;
      },

      setCta: function (ctaCategory) {
        var changed = this.cta_category !== ctaCategory;
        this.cta_category = ctaCategory;
        current.cta_category = ctaCategory;
        this.setAttribute("data-cta", ctaCategory);
        return changed;
      },

      setTargeting: function (targeting) {
        var changed = this.targeting !== targeting;
        this.targeting = targeting;
        current.targeting = targeting;
        this.element.setAttribute("data-targeting", targeting);
        this.renderActionBar();
        return changed;
      },

      // When a component is added to a section, it can
      // be resized automatically depending on defaults.
      // That logic lives here. Note this does no validation
      setInitialColumns: function (minColumns) {
        var columns = this.config.columns;
        if (this.section) {
          columns = this.columns();
        }
        if (minColumns > columns) {
          columns = minColumns;
        }
        this.setColumns(columns);
      },

      render: function (context) {
        var me = this;
        var context = context || this.context;

        // Allow content rendered server side by modifiers to be retained if not overridden by client side content
        // Note that we must avoid removing context values that were rendered server-side and replacing them with the
        // raw value, which would prevent ResourceTemplateModifier values from being rendered client-side since the
        // modified content is stored under the same attribute name.
        var renderContext = jQuery.extend({}, context);
        var resourceTemplateAttributes = Component.serverRenderedAttributes(component);
        jQuery.each(component.rendered.context, function (key, value) {
          if (resourceTemplateAttributes.indexOf(key) == -1) {
            return;
          }
          renderContext[key] = value;
        });

        // render a reasonable default for an empty component
        if (me.empty) {
          var emptyTemplate = findTemplate("empty-component");
          me.element.innerHTML = emptyTemplate(me.config);
        } else {
          this.element.classList.remove("empty-component");
          // Use jQuery to set HTML so scripts tags are executed automatically
          jQuery(me.element).html(this.template(Component.decorateRenderContext(component, deepCopy(renderContext))));
        }

        me.renderActionBar();

        // don't remove classes, so you can add classes directly
        // to the element and they won't get removed every time
        // render is called
        current.classes.forEach(function (className) {
          me.element.classList.add(className);
        });
        this.element.classList.add("component");

        Event.trigger("pb:component:render", me);
        // Notify the element itself so the component's behavior js can know when the component rerenders.
        // We need to use the jQuery from the preview iframe or the event won't be detected. If jQuery isn't loaded in
        // the preview iFrame, don't worry about triggering the event because nobody will be listening for it yet.
        var iframeJQuery = me.element.ownerDocument.defaultView.$;
        if (iframeJQuery) {
          iframeJQuery(me.element).trigger("pb:component:render");
        }

        return this.element.innerHTML;
      },

      renderActionBar: function () {
        var component = this;
        actionBar.clearActions();

        if (component.inherited) {
          actionBar.addAction("remove", {
            tooltip: Sparkle.t("admin.components.messages.hide_inherited"),
            icon: createIconTag("button-hide"),
            callback: function () {
              Component.removeWithConfirmation(component);
            },
          });
        } else {
          actionBar.addAction("remove", {
            tooltip: Sparkle.t("admin.components.messages.remove"),
            icon: createIconTag("close-alt-color"),
            callback: function () {
              Component.removeWithConfirmation(component);
            },
          });
        }

        actionBar.addBadge("targeting", {
          tooltip: Sparkle.t("admin.components.messages.targeted"),
          icon: createIconTag("target"),
          condition: function () {
            return component.data("targeting");
          },
        });

        actionBar.addBadge("position", {
          tooltip: Sparkle.t("admin.components.messages.positioned"),
          icon: createIconTag("pin"),
          condition: function () {
            return component.data("position") != null;
          },
        });

        actionBar.addBadge("unsynced", {
          tooltip: Sparkle.t("admin.components.messages.unsynced"),
          icon: createIconTag("shared-content"),
          condition: function () {
            return component.rendered.unsynced;
          },
        });

        jQuery(this.element).prepend(actionBar.render().el);
      },

      /**
       * Tools work with components, but they only work with a piece of the component context.
       * This function creates a splinter of the parent Component that only has the piece of the
       * context specified in the rendered markup of the component, specifically the data-context-name
       * attribute.
       *
       * @memberof page-builder.Component
       * @constructs Splinter
       * @param {string} name - The name of the piece of the context for this splinter
       * @returns Splinter
       */
      splinter: function (name) {
        var splinter = Object.create(this),
          parent = this;

        if (name) {
          splinter.context = deepCopy(pluck(name, parent.context));
          splinter.name = name;
        } else {
          splinter.context = null;
        }

        /**
         * Saves any changes to the splinter's context to the component's
         * context and sets the splinter context to a new copy, ready for
         * more changes.
         * @memberof page-builder.Component.Splinter
         */
        splinter.save = function () {
          var name,
            data,
            splinter = this;

          if (arguments.length == 0) {
            name = splinter.name;
            data = this.context;
          } else if (arguments.length == 1) {
            name = splinter.name;
            data = arguments[0];
          } else if (arguments.length === 2) {
            name = arguments[0];
            data = arguments[1];
          } else {
            throw Error("Arguments length for splinter.save exceeded: " + arguments.length);
          }

          if (name === splinter.name) {
            splinter.context = deepCopy(data);
            parent.save(name, this.context);
          } else {
            deepUpdate(name, this.context, data);
            parent.save(splinter.name + "." + name, data);
          }
        };
        /**
         * Rollback changes to the last save point and then reset
         * the splinter's context value.
         */
        splinter.undo = function () {
          parent.undo();
          this.reset();
        };
        /**
         *
         */
        splinter.render = function () {
          var original = pluck(name, parent.context),
            splinter = this;
          deepUpdate(name, parent.context, splinter.context);
          var markup = parent.render.apply(parent, arguments);
          deepUpdate(name, parent.context, original);
          return markup;
        };

        splinter.reset = function () {
          this.context = deepCopy(pluck(name, parent.context));
          Event.trigger("pb:splinter:" + this.name + ":reset");
        };
        return splinter;
      },

      /**
       * Check to see if a component has been changed.
       * @memberof page-builder.Component
       */
      changed: function (stamp) {
        stamp = stamp || "head";
        if (history[history.length - 1].stamps.indexOf(stamp) > -1) {
          // nothing has happened in this edit session
          return false;
        } else {
          // there have been changes in this edit session
          return true;
        }
      },
      /**
       * Save the current context. Adds it to the history stack for undo. Creates
       * a new snapshot of the current context for the component and splinters to
       * work with.
       * @memberof page-builder.Component
       */
      save: function (name, data) {
        // if we're saving anything at all, assume that
        // we've at least changed one of the initial values
        // so mark it as non-empty.
        this.empty = false;

        //  modify the updated_at timestamp accordingly to show that this
        //   component has been altered.
        this.updated_at = current.updated_at = moment().toString();

        // some splinters don't have a context, so there will be no data here
        if (name) {
          deepUpdate(name, current.context, data);
        }
        history.push({ stamps: [], snapshot: current });

        current = createSnapshot(current);
        this.context = current.context;
        this.config.classes = current.classes;

        Event.trigger("pb:component:save");
      },

      deleteData: function (name) {
        if (name) {
          deepDelete(name, current);
          this.save();
        }
      },

      /**
       * Undoes all of the current changes to a component and rolls back
       * to the most recently saved snapshot in the history stack. Updates
       * all splinters.
       * @memberof page-builder.Component
       */
      undo: function () {
        if (history.length > 1) {
          history.pop();
          var last = history[history.length - 1],
            current = createSnapshot(last.snapshot);
          this.context = current.context;
        }
      },
      /**
       * Rollback history to the last 'flush' point and update all splinters
       * with new values for context.
       * @memberof page-builder.Component
       */
      rollback: function (stamp) {
        stamp = stamp || "head";
        // rollback history
        while (history.length > 1) {
          var snapshot = history.pop();
          if (snapshot.stamps.indexOf(stamp) > -1) {
            history.push(snapshot);
            break;
          }
        }
        current = createSnapshot(history[history.length - 1].snapshot);
        this.context = current.context;
      },
      /**
       * Add the provided stamp value to the list of stamps for the latest
       * history item
       * @memberof page-builder.Component
       */
      stamp: function (stamp) {
        // loop through the current history entries and remove the stamp
        // if it exists
        for (var i = 0; i < history.length; i++) {
          var position = history[i].stamps.indexOf(stamp);
          if (position > -1) {
            history[i].stamps.splice(position, 1);
            break;
          }
        }
        history[history.length - 1].stamps.push(stamp);
      },
    };

    return component;
  } // end Component

  /**
   * Decorators may adjust the throwaway context a component is rendered with —
   * e.g. deriving display-only fields the templates need but the serialized page
   * must not carry. They run on the render's deep copy, so decorations are
   * ephemeral by construction.
   */
  Component.renderContextDecorators = [];

  Component.addRenderContextDecorator = function (decorate) {
    Component.renderContextDecorators.push(decorate);
  };

  Component.decorateRenderContext = function (component, context) {
    Component.renderContextDecorators.forEach(function (decorate) {
      decorate(component, context);
    });
    return context;
  };

  function Blended(original) {
    function setOriginalData(value) {
      var id = original.data("id"),
        uncustomized = Component.uncustomized;
      if (uncustomized[id]) {
        uncustomized[id] = value;
      } else {
        uncustomized[original.key] = value;
      }
      return value;
    }

    function originalData() {
      // new shared components added to the page are added
      // to uncustomized cache with ID as the key since the server
      // doesn't return a key
      var id = original.data("id"),
        uncustomized = Component.uncustomized;
      return uncustomized[id] || uncustomized[original.key];
    }

    var component = Object.create(original);

    // we need a way of clearing the override data without removing the
    // class elements, and the `Blended` decorator doesn't have `current`
    // in scope.
    component.clearOverrideData = function () {
      var current = original.getCurrent();

      // clear override context.
      for (var prop in original.context) {
        delete original.context[prop];
      }

      // clear override classes.
      current.classes.length = 0;

      // clear override cta settings.
      delete current.cta_category;
      delete current.type;
    };

    // don't actually add classes to this blended component if the
    // class we're trying to add is the same as the shared component.
    component.addClass = function (className) {
      if (!_.contains(component.sharedData().classes, className)) {
        original.addClass(className);
      } else {
        if (this.element) {
          this.element.classList.add(className);
        }
      }
    };

    // return a list of all classes that correspond to appearance
    // settings for this component.
    function appearanceClasses() {
      return _.flatten(
        _.map(component.config.options, function (value) {
          return _.keys(value);
        }),
      );
    }

    // return the list of classes for this blended component by
    // starting with the shared component and replacing any
    // classes that might be overridden on this page.
    function blendClasses(base, overrides) {
      // first, there are some classes applied to a component that don't
      // correspond to individual appearance settings.
      var blendedClasses = _.difference(component.sharedData().classes, appearanceClasses());

      // then for appearance settings, we need to make sure we're only
      // including one class per appearance setting.
      for (var key in component.config.options) {
        blendedClasses.push(inlineClassForKey(key) || sharedClassForKey(key));
      }

      // clean up any null values.
      return _.compact(blendedClasses);
    }

    function blend(base, overrides) {
      base = deepCopy(base);

      for (var prop in overrides) {
        if (overrides.hasOwnProperty(prop)) {
          // recurse only on context, don't want to completely
          // override the context, just each individual attribute
          if (prop === "context") {
            base.context = blend(base.context, overrides.context);
          } else if (prop === "classes") {
            base.classes = blendClasses(base.classes, overrides.classes);
          } else {
            base[prop] = overrides[prop];
          }
        }
      }
      return base;
    }

    component.markHidden = function () {
      this.data("hidden", true);
      this.hidden = true;
      this.save();
    };

    component.unmarkHidden = function () {
      this.deleteData("hidden");
      this.hidden = false;
    };

    // context is passed around and used for rendering, so it needs
    // to represent the combination of shared and overriden data
    // it will also need to be updated on save. save will update the
    // overrides automatically, but not the instance level context variable
    component.context = blend(originalData().context, original.context);
    if (!original.columns) {
      component.columns = originalData().columns;
    }

    component.blendedData = function () {
      return blend(originalData(), original.data());
    };

    component.sharedData = function () {
      return originalData();
    };

    component.isContextCustomized = function () {
      // if this is a splinter, we have to compare differently.
      if (this.name) {
        var contextToCompare = this.context;

        // if a tool has an object as it's shared data, the comparison is a little more complicated.
        // we'll generate a new contextToCompare.
        if (component.sharedData().context[this.name] instanceof Object) {
          // get the list of keys present in the shared version.
          var sharedKeys = _.keys(component.sharedData().context[this.name]);

          // now filter the blended version of the data for only those keys.
          contextToCompare = _.pick(
            this.context,
            function (value, key, object) {
              return _.indexOf(sharedKeys, key) !== -1;
            }.bind(this),
          );
        }

        // if these values match, it's not customized.
        return !_.isEqual(component.sharedData().context[this.name], contextToCompare);
      } else {
        // we can't do this comparison yet.
        return false;
      }
    };

    component.isKeyCustomized = function (key) {
      return !!inlineClassForKey(key);
    };

    function inlineClassForKey(key) {
      return _.intersection(original.getClasses(), classesForKey(key))[0];
    }

    function sharedClassForKey(key) {
      return _.intersection(component.sharedData().classes, classesForKey(key))[0];
    }

    component.restoreClass = function (key) {
      // remove all possible values for this setting.
      classesForKey(key).forEach(function (className) {
        component.removeClass(className);
      });

      // add the shared class key back in the list.
      component.addClass(sharedClassForKey(key));
    };

    function classesForKey(key) {
      return _.keys(component.config.options[key]);
    }

    function findKeyForClass(className) {
      for (var key in component.config.options) {
        if (_.contains(classesForKey(key), className)) {
          return key;
        }
      }
      return null;
    }

    component.pushClass = function (className) {
      var key = findKeyForClass(className);
      if (key) {
        // remove all possible values for this key from the shared component.
        component.sharedData().classes = _.difference(component.sharedData().classes, classesForKey(key));

        // add the new class.
        component.sharedData().classes.push(className);
        saveShared();
      }
    };

    function hasValues(object) {
      return _.without(_.values(object), null).length > 0;
    }

    function calculateRestoreValue(restoreValue) {
      if (_.isObject(restoreValue)) {
        if (hasValues(restoreValue)) {
          return deepCopy(restoreValue);
        } else {
          return null;
        }
      } else {
        return restoreValue;
      }
    }

    // overwrite the customization with the value from the shared component.
    component.restore = function () {
      if (this.name) {
        this.save(this.name, calculateRestoreValue(component.sharedData().context[this.name]));
      } else {
        // we don't support restoring the entire component yet.
      }
    };

    function saveShared() {
      // save the shared component back to the server immediately.
      var url = "/admin/components/" + original.data().id + ".json";
      return Sparkle.PageBuilder.Persistence.putJSON(url, {
        component: component.sharedData(),
        sync_action: "copy",
      }).fail(function (jqXHR) {
        // This is handled via the promise's `fail` callback to ensure it is called before subsequent callbacks are
        // evaluated instead of after as would happen if we used the `statusCode` AJAX option instead.
        if (jqXHR.status == 409) {
          component.rendered.unsynced = true; // Sync confirmation required
        }
      });
    }

    component.push = function () {
      var promise;

      if (this.name) {
        // overwrite the shared component value with the customized value.
        component.sharedData().context[this.name] = this.context;
        this.save(this.name, null);
        promise = saveShared();
      } else {
        var blendedData = deepCopy(component.blendedData());

        // there are a few data values that are only stored inline.
        delete blendedData.key;
        delete blendedData.columns;
        delete blendedData.replaces;

        // overwrite the entire
        // this actually breaks the interface where they passed in a method
        // telling us how to get the original data. that method doesn't
        // allow us to *set* the original data, as we need to here, so we're
        // going around it. we should revisit that interface.
        setOriginalData(blendedData);
        promise = saveShared();

        // now that our inline overrides are the default values, we need
        // to remove the inline overrides so we can inherit future changes
        // to the shared component from other pages. however, we don't
        // call methods that would also disrupt their element classes.

        // clear out the inline classes.
        component.clearOverrideData();
      }

      return promise;
    };

    component.save = function (name, data) {
      // needs context prefix to save to the context
      // property of the data object.
      var dataName = "context." + name,
        baseData = pluck(dataName, originalData());

      // shared components don't want to save redundantly,
      // because it overrides. we only want to override
      // if it's different.
      // IMPORTANT: the != sign is used here over
      // !== because if we save with null and the current
      // value is undefined, we do NOT want to save, so this
      // will see undefined and null as equal, which we want in
      // this case.
      var emptyValue = data === "" || _.isUndefined(data) || _.isNull(data);

      if (emptyValue && !baseData) {
        // if we're down to an empty string or null or undefined, and there's nothing
        // on the base data, just delete it from the inline data,
        // don't want this attribute overridden with nothing forever
        // if they add content to the base component.
        original.deleteData(dataName);
      } else if (_.isEqual(baseData, data)) {
        // if it's exactly the same, don't save the override
        // We use a function to test equality so complex object values are supported
        original.deleteData(dataName);
      } else if (baseData != data) {
        original.save(name, data);
      }

      this.context = blend(originalData().context, original.context);
    };

    component.render = function (context) {
      context = context || this.context;

      var data = originalData(),
        sharedContext = blend(data.context, context);

      // FIXME
      // this doesn't feel like the right place to fix this issue, but we're
      // running into an issue where when we add a new shared component to
      // the page, the cta category hasn't somehow been set by now. i'm not
      // 100% sure where the right place is to fix that problem, so i'm doing
      // it here, right before we render the component. we can move this.
      if (this.blendedData().type === "CallToAction") {
        component.setCta(this.blendedData().cta_category);
      }

      return original.render.call(this, sharedContext);
    };

    component.getClasses = function () {
      // return the blended classes.
      return component.data("classes");
    };

    component.data = function (name, value) {
      if (name && _.isUndefined(value)) {
        return pluck(name, blend(originalData(), this.data()));
      } else {
        var result = original.data(name, value);

        // FIXME
        // i _hate_ this, but there is another FIXME bug fix where we're
        // explicitly setting this category on a component, and it's
        // causing it to show up in these results and get persisted
        // as part of the overrides. we need to track down the original bug,
        // but haven't been able to.
        if (!name) {
          if (result.type == originalData().type) {
            delete result.type;
          }

          if (result.cta_category == originalData().cta_category) {
            delete result.cta_category;
          }

          // we don't allow people to customize the slides in slideshows.
          // if they want to do this, they need to convert it into a unique
          // component.
          if (result.template == "slideshow") {
            delete result.context.slider;
          }
        }

        return result;
      }
    };

    return component;
  }

  function Shared(component) {
    var data = component.data(),
      id = data.id,
      key = data.key,
      original = Blended(component),
      shared = Object.create(original);

    function otherInstances() {
      return Shared.instances.filter(function (instance) {
        return instance.id === id && instance.key !== key;
      });
    }

    shared.shared = true;
    shared.id = id;
    shared.original = original;

    shared.save = function (name, data) {
      original.save.call(this, name, data);
      otherInstances().forEach(function (instance) {
        instance.original.save.call(instance, name, data);
      });
    };

    shared.render = function (context) {
      var markup = original.render.call(this, context);
      otherInstances().forEach(function (instance) {
        instance.original.render.call(instance, context);
      });

      return markup;
    };

    return shared;
  }

  // store all shared component instances so we can update duplicates
  // on saves and renders
  Shared.instances = [];

  // remove instances of shared components from the instances array
  // if they are removed from section
  // we update the instances based on Section events so that way we know the
  // difference between a component being deleted vs. just moved to another section.
  // so if it's moved to another section, it will be removed from instances but then
  // added back. if it's just removed, it won't be added back.
  // If we add to Shared.instances only in the Shared function, we wouldn't add back
  // if a component is just moved.
  Event.on("pb:section:remove", function (component) {
    var index = Shared.instances.indexOf(component);
    if (index > -1) {
      Shared.instances.splice(index, 1);
    }
  });
  Event.on("pb:section:add", function (component) {
    if (component.shared) {
      Shared.instances.push(component);
    }
  });

  // format shared component lightweight data
  function format(componentData) {
    return {
      description: componentData.description || "need a description",
      id: componentData.id,
      role: componentData.role,
      type: componentData.type,
      icon_name: componentData.icon_name,
      cta_category: componentData.cta_category,
      template: componentData.template,
      template_name: window.COMPONENTS[componentData.template]?.name,
      name: componentData.name,
      created_at: moment(componentData.created_at).format("MM/DD/YYYY"),
      created_at_timestamp: componentData.created_at,
      updated_at: moment(componentData.updated_at).format("MM/DD/YYYY"),
      updated_at_timestamp: componentData.updated_at,
      active: componentData.active,
      columns: componentData.columns,
      label_ids: componentData.label_ids,
      component_assignments_count: componentData.component_assignments_count,
    };
  }

  function updateCaches(data, caches) {
    var stripped = format(data);

    caches.lightweightData = _.reject(caches.lightweightData, function (component) {
      return component.id === data.id;
    });
    caches.lightweightData.unshift(stripped);

    caches.lightLookup[data.id] = stripped;
    caches.data[data.id] = data;
    Component.uncustomized[data.id] = data;
  }

  var SharedComponent = {
    create: function (sharedData, config) {
      return Component.create(
        {
          key: sharedData.key,
          id: sharedData.id,
          template: sharedData.template,
          classes: sharedData.classes || [],
          context: {},
        },
        config,
      );
    },

    load: function (componentsData) {
      // create a list of shared components from the data
      var data = [];
      if (_.isObject(componentsData)) {
        _.values(componentsData).forEach(function (components) {
          data = data.concat(components);
        });
        data = data.filter(function (componentData) {
          return !!componentData.id;
        });
      }

      // fetch more detailed data for each
      var deferred = data.map(function (data) {
        return jQuery.get("/admin/components/" + data.id + ".json").then(function (response) {
          SharedComponent.data[response.id] = response;
        });
      });

      return jQuery.when.apply($, deferred);
    },

    lightweight: function (id) {
      return SharedComponent.lightLookup[id] || null;
    },

    fetch: function (id) {
      var deferred = jQuery.Deferred();

      jQuery.get("/admin/components/" + id + ".json").then(function (response) {
        updateCaches(response, SharedComponent);
        deferred.resolve(response);
      });

      return deferred.promise();
    },

    fetchAll: function (reload) {
      var deferred = jQuery.Deferred();

      if (reload || _.isEmpty(SharedComponent.lightweightData)) {
        jQuery
          .get("/admin/components.json")
          .then(function (response) {
            // temporarily convert data to lighweight version
            SharedComponent.lightweightData = response.map(format);
            SharedComponent.lightweightData.forEach(function (data) {
              SharedComponent.lightLookup[data.id] = data;
            });
            deferred.resolve(SharedComponent.lightweightData);
          })
          .fail(function (err) {
            console.error("AJAX call failed");
            deferred.reject(err);
          });
      } else {
        deferred.resolve(SharedComponent.lightweightData);
      }
      return deferred.promise();
    },
    data: {},

    // the api returns a set of data in a specific order which then gets
    //  manipulated and put out of order. this original data allows us to keep
    //  things in order so that they are displayed correctly.
    lightweightData: [],
    lightLookup: {},

    /**
     * @name inline
     * @description
     *  Convert a shared component into an inline one by triggering a
     *   replacement through the page builder api.
     * @param {SharedComponent} component
     * @param {object} pageConfig
     */
    inline: function (component, pageConfig) {
      var data = component.blendedData();

      delete data.id;

      var newComponent = Sparkle.PageBuilder.Component.create(data, pageConfig);

      // instead of making _another_ request to the server for the form's
      // rendered html, we're able to reuse the rendered html from the
      // shared component, since nothing on the form as changed yet.
      jQuery(newComponent.element).html(jQuery(component.element).html());

      // just incase there _is_ something worth rendering for, this will
      // make sure that happens.
      newComponent.render();

      // FIXME
      // again, there is an oversight somewhere in that component create process
      // where the cta category isn't being properly set. i need to dig in and
      // figure that out, but this fixes it for now. getting closer on this.
      if (data.type === "CallToAction") {
        newComponent.setCta(data.cta_category);
      }

      return newComponent;
    },

    /**
     * @name convertToShared
     * @description
     *  Given a new name for the shared component, and the subsequent data, this
     *   will go ahead and return a promise that when resolved will create a
     *   shared component.
     * @param {string} name
     * @param {object} componentData
     *
     * @return {Promise.<object>}
     */
    convertToShared: function (name, componentData, pageConfig) {
      var deferred = jQuery.Deferred();

      if (!componentData.key) {
        componentData.key = hex() + "";
      }

      jQuery.ajax({
        url: "/admin/components.json",
        type: "post",
        contentType: "application/json",
        data: JSON.stringify({
          component: _.extend(componentData, { active: true, name: name }),
        }),
        error: function (response) {
          deferred.resolve(response.responseJSON);
        },
        success: function (response) {
          updateCaches(response, SharedComponent);

          // the server is returning the data for the shared version of this
          // component, but we're creating an on-page instance, so we want
          // to create an empty inline component so it defaults to the
          // shared component attributes.
          var newInlineData = { id: response.id, template: response.template, classes: [] };
          var newSharedComponent = Component.create(newInlineData, pageConfig);

          // Add shared component classes to new container element
          response.classes.forEach(function (className) {
            newSharedComponent.addClass(className);
          });

          newSharedComponent.render();
          deferred.resolve(newSharedComponent);
        },
      });

      return deferred.promise();
    },
  };

  /**
   * Most of the time a component is retrieved from section.components, but
   * from time to time we need a way to go from a component element to the component
   * instance.
   * @param {HTMLElement} element - the component element
   * @returns {Component}
   */
  Component.find = function (element) {
    if (element) {
      var key = element.id.split("_")[1],
        section = Section.find(element),
        component;
      // Sometimes there is no data for a component loaded, as is the case
      // for a page with inherited components. and sometimes those pages
      // don't even have a section, so we check that here.
      if (section) {
        for (var i = 0; i < section.components.length; i++) {
          component = section.components[i];
          if (component.key === key) {
            return component;
          }
        }
      }
    }
    return false;
  };

  // Returns the names of the attributes whose content the server renders. The server lists them
  // per component key, which a component added since the last save does not have yet.
  Component.serverRenderedAttributes = function (component) {
    var listedByServer = Component.metadata.resource_template_attributes[component.key];
    if (listedByServer) {
      return listedByServer;
    }

    var attributes = component.config.attributes || {};
    return Object.keys(attributes).filter(function (name) {
      return attributes[name] && !_.isEmpty(attributes[name].resource_template);
    });
  };

  ((Component.each = function (fn) {
    var components = [];
    var sections = Section.instances;

    Object.keys(sections).forEach(function (key) {
      var section = sections[key];
      components = components.concat(section.components);
    });

    components.forEach(fn);
  }),
    (Component.expandAttribute = function (attribute) {
      if (attribute) {
        if ("default" in attribute) {
          return deepCopy(attribute.default);
        }
      }
    }));

  Component.fromConfig = function (id, pageConfig, replaces) {
    var config = pageConfig[id],
      data = {
        key: hex(),
        classes: deepCopy(config.classes) || [],
        template: config.id,
        columns: config.columns,
        cta_category: config.cta_category || false,
        publishing_domain: config.publishing_domain,
        type: config.type,
        context: {},
        sticky: config.sticky || false,
        targeting: config.targeting || false,
        replacement_strategy: config.replacement_strategy || false,
      };

    //  set the default timestamps on component creation
    data.created_at = data.updated_at = moment().toString();

    // set context from attributes
    _.keys(config.attributes).forEach(function (attrName) {
      var attribute = config.attributes[attrName];
      data.context[attrName] = Component.expandAttribute(attribute);
    });

    var element = Component.createElement(data.key, config.id, data.classes);

    var component = Component.create(data, pageConfig, element, replaces);
    component.setColumns(config.columns);
    // set the initial state of a component created from configuration to empty
    component.empty = true;
    component.element.classList.add("empty-component");
    return component;
  };

  Component.createElement = function (key, template, classes) {
    classes = classes || [];
    var element = document.createElement("section");
    element.id = "component_" + key;
    element.dataset.componentTemplate = template;
    element.classList.add("component--" + template.replace(/_/g, "-"));

    classes.forEach(function (className) {
      element.classList.add(className);
    });

    return element;
  };

  // create components from page data or expanded defaults
  // from configuration. Shared components see SharedComponent.create

  Component.create = function (data, config, element, replaces) {
    var component,
      baseData = Component.uncustomized[data.key],
      key = data.key;

    if (data.id) {
      // adding a new shared component to the page
      // create a new unique key
      if (!data.key) {
        data.key = hex() + "";
      }
    }

    // todo: template is not currently saved inline, need to migrate this ideally
    if (!data.template) {
      data.template = baseData.template;
    }

    // set columns if none are set, this is specifically for dragging new
    // shared component onto the page.
    if (!data.columns && !baseData) {
      data.columns = config[data.template].columns;
    }

    if (replaces) {
      data.replaces = replaces;
    }

    if (!element) {
      element = Component.createElement(data.key, data.template, data.classes);
    }

    component = Component(data, config[data.template], element);

    // set to empty object for new components.
    component.rendered = Component.rendered[data.key] || { context: {} };
    component.uncustomized = Component.uncustomized[data.key] || { context: {} };
    component.key = data.key;

    expandImagesInContext(component.context, component.rendered.context, component.config);
    expandImagesInContext(component.uncustomized.context, component.rendered.context, component.config);

    // add special behavior for shared components
    // and form components
    if (data.inherited) {
      component = Blended(component);
    } else if (data.id) {
      component = Shared(component, baseData);
    }

    if (component.inherited) {
      jQuery(component.element).attr("data-page-relation", "inherited");
    } else if (component.shared) {
      jQuery(component.element).attr("data-page-relation", "shared");
    } else {
      jQuery(component.element).attr("data-page-relation", "unique");
    }

    if (component.config.renders_form) {
      component = decorateFormComponent(component, deepCopy);
    }

    return component;
  };

  Component.removeWithConfirmation = function (component) {
    var messages = jQuery("<div>");
    var dialogConfig = {
      type: "warning",
      showCancelButton: true,
      confirmButtonColor: "#5aadca",
      confirmButtonText: "Confirm",
      cancelButtonText: "Cancel",
      closeOnConfirm: true,
      closeOnCancel: true,
      html: true,
    };

    var customizations = Component.metadata.customizations[component.key] || [];
    var dependentPageCount = customizations.length;

    if (dependentPageCount) {
      var pages = "" + dependentPageCount + " " + (dependentPageCount == 1 ? "page" : "pages");
      messages.append("<p>This component is inherited by " + pages + ":</p>");

      var dependentPageLinks = jQuery('<ul class="inherited-component-dependency-warning-list">');
      jQuery.each(customizations, function () {
        var listItem = jQuery('<li class="inherited-component-dependency-warning-list-item">');
        listItem.append(jQuery("<a>", { href: this.edit_url, target: "_blank" }).text(this.name));
        dependentPageLinks.append(listItem);
      });
      messages.append(dependentPageLinks);
      messages.append("<p>Removing it will also remove it from those pages.</p>");
    }

    if (component.inherited) {
      dialogConfig.title = "Hide Inherited Component";
      messages.append(
        "<p>Are you sure you want to hide this component? (components can be un-hidden from the main sidebar)</p>",
      );
    } else {
      dialogConfig.title = "Confirm Delete";
      messages.append("<p>Are you sure you want to delete this component?</p>");
    }

    dialogConfig.text = messages.html();

    Sparkle.PageBuilder.Dialog.show(dialogConfig, function () {
      Component.remove(component);
    });
  };

  Component.remove = function (component) {
    component.section.completeRemove(component);
    component.row.remove(component);
    jQuery(component.element).remove();
    component.section.reflow();
  };

  // export
  // VITE_MIGRATION: preserve sub-properties (e.g. ActionBar) attached to
  // Sparkle.PageBuilder.Component by glob-imported child modules under
  // models/component/. Under ESM those children's import.meta.glob is
  // hoisted above this file's body, so they run BEFORE this assignment
  // and the bare `Component = Component` would otherwise wipe them out.
  var _previousComponent = Sparkle.PageBuilder.Component;
  Sparkle.PageBuilder.Section = Section;
  Sparkle.PageBuilder.Component = Component;
  if (_previousComponent) {
    Object.assign(Sparkle.PageBuilder.Component, _previousComponent);
  }
  Sparkle.PageBuilder.Page = Page;
  Sparkle.PageBuilder.SharedComponent = SharedComponent;
})(
  Sparkle.PageBuilder.Row,
  Sparkle.PageBuilder.Helpers.assert,
  Sparkle.PageBuilder.Helpers.deepCopy,
  Sparkle.PageBuilder.Helpers.numColumns,
  Sparkle.PageBuilder.Templates.findTemplate,
  Sparkle.PageBuilder.Helpers.Event,
  window.moment,
  Sparkle.PageBuilder.Helpers.toolKeysForComponent,
  window._,
);
